import { NetworkStatus } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getFeatureFlags } from 'core/userContext';
import { WORKFLOW_TRIGGERS } from 'modules/workflows';
import { SimpleOverlayContainer } from 'modules/workflows/components/NodeEditor/ResizableOverlay';
import { Workflow } from 'modules/workflows/types';
import { FormField } from 'ui/formField';
import { InfoBanner } from 'ui/banner';
import { Tooltip } from 'ui/tooltip';
import { BasicSelect } from 'ui/selects';
import LinkedWorkflow from '../../../StartNodeEditor/OtherJourneyEntrySource/LinkedWorkflow/LinkedWorkflow';
import { LinkedWorkflowWrapper } from './TriggerTypeInput.styles';
import { getTriggerTypeOptions } from './TriggerTypeInput.utils';

const displayText = {
  placeholder: 'Select a trigger type...',
  readOnlyTriggerTypeTooltip:
    'This trigger type can\'t be changed because other journeys are linked to it. To change this trigger type, first update or remove the "Send to Journey" nodes in those journeys.',
};

type TriggerTypeInputProps = {
  value: string;
  onChange: (value: string) => void;
  betaNodes: string[];
  currentWorkflow: Workflow;
  showOverlay?: boolean;
  isTriggeredByWorkflow?: boolean;
  isReadOnly?: boolean;
};

const FullWidthTooltip = styled(Tooltip)`
  width: 100%;
`;

/**
 * NOTE: No longer used after StartTileRefresh is GA
 */
export const TriggerTypeInput = ({
  value,
  onChange,
  betaNodes,
  currentWorkflow,
  showOverlay = false,
  isTriggeredByWorkflow = false,
  isReadOnly = false,
}: TriggerTypeInputProps) => {
  const [isTooltipShown, setIsTooltipShown] = useState(false);
  const featureFlags = useSelector(getFeatureFlags);

  const options = useMemo(
    () =>
      getTriggerTypeOptions(betaNodes).filter(
        (trigger) =>
          featureFlags.ClickedAnSMSTriggerUI || trigger.id !== WORKFLOW_TRIGGERS.CLICKED_AN_SMS,
      ),
    [betaNodes, featureFlags.ClickedAnSMSTriggerUI],
  );

  return (
    <>
      <SimpleOverlayContainer showOverlay={showOverlay} showSpinner={false}>
        <FormField labelText="Entry source" inputId="trigger-selection-field" hasError={false}>
          <FullWidthTooltip
            data-test="read-only-trigger-type-tooltip"
            tooltipContent={<div>{displayText.readOnlyTriggerTypeTooltip}</div>}
            isEnabled={isTooltipShown}
          >
            <BasicSelect
              data-test="basic-select-trigger-type"
              defaultValues={value.length ? [value] : []}
              id="trigger-type"
              onSelect={([selectedTriggerType]) => onChange(selectedTriggerType || '')}
              options={options}
              placeholder={displayText.placeholder}
              isDisabled={isReadOnly || isTooltipShown}
              isFullWidth
            />
          </FullWidthTooltip>
        </FormField>
      </SimpleOverlayContainer>
      {isTriggeredByWorkflow && (
        <LinkedWorkflowWrapper>
          <LinkedWorkflow
            currentWorkflow={currentWorkflow}
            onLoadStatusUpdate={({ networkStatus, workflows }) => {
              setIsTooltipShown(
                isTriggeredByWorkflow &&
                  !!workflows.length &&
                  networkStatus === NetworkStatus.ready &&
                  !isReadOnly,
              );
            }}
            emptyStateComponent={
              <InfoBanner title={'No journeys linked yet'}>
                To send contacts from other journeys to this one, add a &quot;Send to Journey&quot;
                node to those journeys.
              </InfoBanner>
            }
          />
        </LinkedWorkflowWrapper>
      )}
    </>
  );
};
